import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import { subscriptionList } from "../../../redux/actions/payment/subscriptionList";
import { subscriptionAction } from "../../../redux/actions/payment/subscriptionAction";
import { resetSubscriptionAction } from "../../../redux/slices/payment.slice";

const STATUS_META = {
  active:    { label: "Активна",   color: "#22c55e", cls: "tw-bg-green-50 tw-text-green-700 tw-border-green-200" },
  paused:    { label: "На паузе",  color: "#f59e0b", cls: "tw-bg-amber-50 tw-text-amber-700 tw-border-amber-200" },
  cancelled: { label: "Отменена",  color: "#ef4444", cls: "tw-bg-red-50   tw-text-red-600   tw-border-red-100"   },
  expired:   { label: "Истекла",   color: "#cbd5e1", cls: "tw-bg-slate-50 tw-text-slate-500 tw-border-slate-200" },
};

const PERIOD_LABEL = {
  daily: "Ежедневно", weekly: "Еженедельно", biweekly: "Каждые 2 нед.",
  monthly: "Ежемесячно", quarterly: "Ежеквартально", yearly: "Ежегодно",
};

const fmt = (iso) =>
  iso ? new Date(iso).toLocaleDateString("ru-RU", { day: "numeric", month: "short", year: "numeric" }) : "—";

const fmtAmount = (n) => Number(n).toLocaleString("ru-RU") + " ₸";

const Detail = ({ label, value, mono = false }) => (
  <div>
    <p className="tw-text-xs tw-text-gray-400 tw-m-0 tw-mb-0.5">{label}</p>
    <p className={`tw-text-sm tw-font-semibold tw-text-gray-800 tw-m-0 tw-truncate ${mono ? "tw-font-mono tw-text-xs tw-text-gray-500" : ""}`}>
      {value}
    </p>
  </div>
);

const SubscriptionCard = ({ sub, onAction, actionLoading }) => {
  const [confirmCancel, setConfirmCancel] = useState(false);
  const meta = STATUS_META[sub.status] || STATUS_META.expired;
  const isActive  = sub.status === "active";
  const isPaused  = sub.status === "paused";
  const canManage = isActive || isPaused;

  const doAction = (action) => {
    if (action === "cancel" && !confirmCancel) { setConfirmCancel(true); return; }
    setConfirmCancel(false);
    onAction(sub.id, action);
  };

  return (
    <div className="tw-flex tw-rounded-2xl tw-border tw-border-gray-200 tw-overflow-hidden tw-bg-white tw-transition-shadow tw-duration-200 hover:tw-shadow-md">

      {/* Left status bar */}
      <div className="tw-w-1 tw-flex-shrink-0" style={{ background: meta.color }} />

      <div className="tw-flex-1 tw-p-4">

        {/* Top row: name + status + amount */}
        <div className="tw-flex tw-items-center tw-gap-3 tw-mb-3">
          <div className="tw-flex-1 tw-min-w-0">
            <p className="tw-text-sm tw-font-bold tw-text-gray-900 tw-m-0 tw-truncate">
              {sub.catalog_item?.name || "Подписка"}
            </p>
            <p className="tw-text-xs tw-text-gray-400 tw-m-0 tw-mt-0.5">
              {PERIOD_LABEL[sub.billing_period] || sub.billing_period}
            </p>
          </div>
          <div className="tw-text-right tw-flex-shrink-0">
            <p className="tw-text-base tw-font-extrabold tw-text-gray-900 tw-m-0 tw-leading-none">
              {fmtAmount(sub.amount)}
            </p>
            <span className={`tw-inline-flex tw-items-center tw-gap-1 tw-mt-1 tw-px-2 tw-py-0.5 tw-rounded-full tw-text-xs tw-font-semibold tw-border ${meta.cls}`}>
              <span className="tw-w-1.5 tw-h-1.5 tw-rounded-full" style={{ background: meta.color }} />
              {meta.label}
            </span>
          </div>
        </div>

        {/* Details row */}
        <div className="tw-grid tw-grid-cols-2 sm:tw-grid-cols-4 tw-gap-3 tw-rounded-xl tw-bg-gray-50 tw-border tw-border-gray-100 tw-px-4 tw-py-3 tw-mb-3">
          <Detail label="Телефон" value={sub.phone_number || "—"} />
          <Detail label="Следующее списание" value={isActive ? fmt(sub.next_billing_at) : "—"} />
          <Detail label="Подключена" value={fmt(sub.created_at)} />
          <Detail label="ID" value={`#${sub.apipay_subscription_id}`} mono />
        </div>

        {/* Alert: grace period */}
        {sub.in_grace_period && (
          <div className="tw-flex tw-items-start tw-gap-2 tw-px-3.5 tw-py-2.5 tw-rounded-xl tw-bg-amber-50 tw-border tw-border-amber-200 tw-mb-3">
            <svg className="tw-w-4 tw-h-4 tw-text-amber-500 tw-flex-shrink-0 tw-mt-px" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd"/>
            </svg>
            <p className="tw-text-xs tw-font-medium tw-text-amber-800 tw-m-0">
              Льготный период — откройте Kaspi и оплатите счёт для продолжения доступа
            </p>
          </div>
        )}

        {/* Alert: failed attempts */}
        {sub.failed_attempts > 0 && !sub.in_grace_period && canManage && (
          <div className="tw-flex tw-items-start tw-gap-2 tw-px-3.5 tw-py-2.5 tw-rounded-xl tw-bg-red-50 tw-border tw-border-red-200 tw-mb-3">
            <svg className="tw-w-4 tw-h-4 tw-text-red-500 tw-flex-shrink-0 tw-mt-px" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd"/>
            </svg>
            <p className="tw-text-xs tw-font-medium tw-text-red-700 tw-m-0">
              Неудачных попыток оплаты: <strong>{sub.failed_attempts}</strong>
            </p>
          </div>
        )}

        {/* Actions */}
        {canManage && (
          confirmCancel ? (
            <div className="tw-flex tw-items-center tw-gap-2 tw-p-3 tw-rounded-xl tw-bg-red-50 tw-border tw-border-red-200">
              <p className="tw-text-xs tw-font-medium tw-text-red-700 tw-m-0 tw-flex-1">
                Отменить подписку навсегда?
              </p>
              <button onClick={() => doAction("cancel")} disabled={actionLoading}
                className="tw-px-3 tw-py-1.5 tw-text-xs tw-font-bold tw-text-white tw-bg-red-600 tw-rounded-lg hover:tw-bg-red-700 tw-transition-colors disabled:tw-opacity-50">
                Да
              </button>
              <button onClick={() => setConfirmCancel(false)}
                className="tw-px-3 tw-py-1.5 tw-text-xs tw-font-semibold tw-text-gray-600 tw-bg-white tw-rounded-lg tw-border tw-border-gray-300 hover:tw-bg-gray-50 tw-transition-colors">
                Нет
              </button>
            </div>
          ) : (
            <div className="tw-flex tw-items-center tw-justify-between tw-gap-2">
              <div className="tw-flex tw-gap-2">
                {isActive && (
                  <button onClick={() => doAction("pause")} disabled={actionLoading}
                    className="tw-flex tw-items-center tw-gap-1.5 tw-px-3 tw-py-1.5 tw-text-xs tw-font-semibold tw-text-amber-700 tw-bg-amber-50 tw-border tw-border-amber-200 tw-rounded-lg hover:tw-bg-amber-100 tw-transition-colors disabled:tw-opacity-50">
                    <svg className="tw-w-3 tw-h-3" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zM7 8a1 1 0 012 0v4a1 1 0 11-2 0V8zm5-1a1 1 0 00-1 1v4a1 1 0 102 0V8a1 1 0 00-1-1z" clipRule="evenodd"/>
                    </svg>
                    Приостановить
                  </button>
                )}
                {isPaused && (
                  <button onClick={() => doAction("resume")} disabled={actionLoading}
                    className="tw-flex tw-items-center tw-gap-1.5 tw-px-3 tw-py-1.5 tw-text-xs tw-font-semibold tw-text-green-700 tw-bg-green-50 tw-border tw-border-green-200 tw-rounded-lg hover:tw-bg-green-100 tw-transition-colors disabled:tw-opacity-50">
                    <svg className="tw-w-3 tw-h-3" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM9.555 7.168A1 1 0 008 8v4a1 1 0 001.555.832l3-2a1 1 0 000-1.664l-3-2z" clipRule="evenodd"/>
                    </svg>
                    Возобновить
                  </button>
                )}
              </div>
              <button onClick={() => doAction("cancel")} disabled={actionLoading}
                className="tw-text-xs tw-font-medium tw-text-gray-400 hover:tw-text-red-500 tw-transition-colors disabled:tw-opacity-50">
                Отменить подписку
              </button>
            </div>
          )
        )}
      </div>
    </div>
  );
};

const SubscriptionSection = () => {
  const dispatch = useDispatch();
  const {
    subscriptionList: { data: subs, loading, error },
    subscriptionAction: { data: actionData, loading: actionLoading, error: actionError },
  } = useSelector((state) => state.payment);

  useEffect(() => { dispatch(subscriptionList()); }, [dispatch]);

  useEffect(() => {
    if (actionData) {
      toast.success("Подписка обновлена");
      dispatch(resetSubscriptionAction());
      dispatch(subscriptionList());
    }
  }, [actionData, dispatch]);

  useEffect(() => {
    if (actionError) {
      toast.error(actionError?.error || "Ошибка при обновлении подписки");
      dispatch(resetSubscriptionAction());
    }
  }, [actionError, dispatch]);

  if (loading) {
    return (
      <div className="tw-py-6 tw-flex tw-items-center tw-justify-center tw-gap-2.5 tw-text-gray-400">
        <svg className="tw-animate-spin tw-w-4 tw-h-4" fill="none" viewBox="0 0 24 24">
          <circle className="tw-opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/>
          <path className="tw-opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"/>
        </svg>
        <span className="tw-text-sm">Загрузка...</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="tw-py-4 tw-text-center">
        <p className="tw-text-sm tw-text-red-500 tw-mb-2">Не удалось загрузить подписки</p>
        <button onClick={() => dispatch(subscriptionList())}
          className="tw-text-sm tw-text-blue-600 tw-font-medium hover:tw-underline">
          Попробовать снова
        </button>
      </div>
    );
  }

  if (!subs || subs.length === 0) {
    return (
      <div className="tw-py-8 tw-text-center tw-rounded-2xl tw-bg-gray-50 tw-border tw-border-dashed tw-border-gray-200">
        <div className="tw-w-10 tw-h-10 tw-mx-auto tw-mb-3 tw-rounded-full tw-bg-gray-100 tw-flex tw-items-center tw-justify-center">
          <svg className="tw-w-5 tw-h-5 tw-text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="1.5">
            <path strokeLinecap="round" strokeLinejoin="round" d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z"/>
          </svg>
        </div>
        <p className="tw-text-sm tw-font-semibold tw-text-gray-700 tw-mb-1">Нет активных подписок</p>
        <p className="tw-text-xs tw-text-gray-400 tw-max-w-xs tw-mx-auto">
          Подписка создаётся автоматически после первой оплаты тарифа
        </p>
      </div>
    );
  }

  return (
    <div className="tw-space-y-3">
      {subs.map((sub) => (
        <SubscriptionCard
          key={sub.id}
          sub={sub}
          onAction={(id, action) => dispatch(subscriptionAction({ subscriptionId: id, action }))}
          actionLoading={actionLoading}
        />
      ))}
    </div>
  );
};

export default SubscriptionSection;
